// catalogSaga — manages surface freshness checks and offline resilience.
//
// MarkSurfaceStaleOnTimeout: periodically scans all surfaces and marks any whose
// freshness TTL has expired as stale.
//
// Offline resilience: listens for connectionLost/connectionRestored and manages
// catch-up processing state.

import { createAction } from '@reduxjs/toolkit';
import {
  markSurfaceStale,
  connectionLost,
  connectionRestored,
  catchUpCompleted,
  selectAllSurfaces,
} from '../slices/catalogSlice.ts';
import type { CatalogSurface } from '../../types/index.ts';

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export const startFreshnessMonitor = createAction('catalog/startFreshnessMonitor');
export const stopFreshnessMonitor = createAction('catalog/stopFreshnessMonitor');
export const checkFreshnessNow = createAction('catalog/checkFreshnessNow');

// ---------------------------------------------------------------------------
// Pure staleness logic (testable without saga machinery)
// ---------------------------------------------------------------------------

export function isSurfaceExpired(surface: CatalogSurface, now: number): boolean {
  if (surface.freshness.isStale) return false; // already stale
  const lastUpdated = new Date(surface.freshness.lastUpdatedAt).getTime();
  return now - lastUpdated > surface.freshness.ttlMs;
}

export function findExpiredSurfaces(surfaces: CatalogSurface[], now: number): CatalogSurface[] {
  return surfaces.filter((s) => isSurfaceExpired(s, now));
}

// ---------------------------------------------------------------------------
// Catch-up logic — reconnecting does NOT make data fresh.
//
// While disconnected, events may have been missed for ANY surface, so the
// honest catch-up marks every not-yet-stale surface stale (there is no fetch
// action in the client — surfaces only become fresh again when a real
// refreshSurface arrives with new data). The previous implementation
// relabeled stale surfaces as fresh WITHOUT refetching — fabricated freshness.
// ---------------------------------------------------------------------------

export function buildCatchUpStaleActions(surfaces: CatalogSurface[], staleSince: string) {
  return surfaces
    .filter((s) => !s.freshness.isStale)
    .map((s) =>
      markSurfaceStale({
        tenantId: s.tenantId,
        surfaceId: s.surfaceId,
        staleSince,
      }),
    );
}

// ---------------------------------------------------------------------------
// Redux-Saga integration
// ---------------------------------------------------------------------------

// The saga implementation uses redux-saga primitives.
// For testability, the core logic lives in the pure functions above.
// The saga wiring below orchestrates the interval + action dispatching.

import { put, select, take, race, delay, call, all } from 'redux-saga/effects';

function* freshnessCheckCycle(): Generator {
  const surfaces = (yield select(selectAllSurfaces)) as CatalogSurface[];
  const now = Date.now();
  const expired = findExpiredSurfaces(surfaces, now);

  for (const surface of expired) {
    yield put(
      markSurfaceStale({
        tenantId: surface.tenantId,
        surfaceId: surface.surfaceId,
        staleSince: new Date(now).toISOString(),
      }),
    );
  }
}

export function* freshnessMonitorSaga(): Generator {
  while (true) {
    const { stop } = (yield race({
      tick: delay(10000), // check every 10 seconds
      stop: take(stopFreshnessMonitor.type),
      check: take(checkFreshnessNow.type),
    })) as { stop?: unknown };

    if (stop) break;
    yield call(freshnessCheckCycle);
  }
}

export function* connectionResilienceSaga(): Generator {
  while (true) {
    yield take(connectionLost.type);
    // Wait for reconnect
    yield take(connectionRestored.type);

    // Catch-up: while offline any surface may have missed events, so every
    // surface still marked fresh is now of unknown freshness — mark it stale.
    // Surfaces only report fresh again when real data arrives (refreshSurface).
    const surfaces = (yield select(selectAllSurfaces)) as CatalogSurface[];
    const now = new Date().toISOString();
    const actions = buildCatchUpStaleActions(surfaces, now);

    for (const action of actions) {
      yield put(action);
    }

    yield put(catchUpCompleted());
  }
}

export function* catalogSaga(): Generator {
  yield all([call(freshnessMonitorSaga), call(connectionResilienceSaga)]);
}
